const NEW_RUN_PENDING = new Set([
  "queued",
  "running",
  "pending",
  "cloning_repo",
  "editing",
  "building",
  "preview_ready",
]);

export const AGENT_LABELS = {
  coding: "Coding",
  data: "Data",
  ask: "Answer",
  answer: "Answer",
  composite: "Change",
  seo: "SEO",
  sitebuilder: "Site builder",
};

export const AGENT_LABELS_LONG = {
  coding: "Coding agent",
  data: "Data agent",
  ask: "Answer",
  answer: "Answer",
  composite: "Change",
  seo: "SEO agent",
  sitebuilder: "Site builder",
};

export function formatRunLabel(runLabel, fallback = "Agent run") {
  const raw = (runLabel || "").trim();
  if (!raw) return fallback;
  if (raw === "qa") return "Review";
  if (raw === "taste") return "Polish";
  if (raw === "planner") return "Planner";
  if (raw.includes(" ")) {
    return raw
      .split(/\s+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

export function currentAttempt(change) {
  const runs = change?.agent_runs || [];
  const latest = runs[runs.length - 1];
  const live = latest && (latest.status === "running" || latest.status === "pending");
  const stale = latest && !live && NEW_RUN_PENDING.has(change?.status);
  const run = stale ? null : latest;
  if (run) {
    return {
      run,
      logs: run.agent_log || [],
      start: run.started_at || run.create_date,
      end: run.completed_at,
      running: Boolean(live),
    };
  }
  const start = latest ? null : change?.create_date;
  return {
    run: null,
    logs: latest ? [] : change?.agent_log || [],
    start,
    end: latest ? null : change?.completed_at || change?.resolved_at,
    running: Boolean(start && NEW_RUN_PENDING.has(change?.status)),
  };
}

export function runAgentLabel(agentType) {
  return AGENT_LABELS[agentType] || "Agent";
}

export function formatModel(agentConfigOrModel) {
  const model = typeof agentConfigOrModel === "string"
    ? agentConfigOrModel
    : agentConfigOrModel?.model;
  if (!model) return "";
  const withoutProvider = model.replace(/^openrouter\//, "");
  return withoutProvider.includes("/") ? withoutProvider.split("/").pop() : withoutProvider;
}

export function changeRuns(change) {
  return (change?.agent_runs || []).map((run) => ({
    hash: run.hash,
    agentType: run.agent_type,
    agentLabel: runAgentLabel(run.agent_type),
    model: formatModel(run.model || run.agent_config),
    start: run.started_at || run.create_date,
    end: run.completed_at,
    logs: run.agent_log || [],
    usage: run.usage,
    status: run.status,
    running: run.status === "running" || run.status === "pending",
  }));
}

export function runDurationSeconds(run, now = Date.now()) {
  if (!run.start) return 0;
  if (!run.running && run.end == null) return 0;
  const endMs = run.running ? now : new Date(run.end).getTime();
  return Math.max(0, Math.round((endMs - new Date(run.start).getTime()) / 1000));
}

export function changeTotals(change, now = Date.now()) {
  const runs = changeRuns(change);
  if (runs.length) {
    let totalSeconds = 0;
    let anyRunning = false;
    for (const run of runs) {
      totalSeconds += runDurationSeconds(run, now);
      if (run.running) anyRunning = true;
    }
    return {
      usage: change?.usage,
      totalSeconds,
      running: anyRunning,
    };
  }

  const attempt = currentAttempt(change);
  if (!attempt.start) {
    return { usage: change?.usage, totalSeconds: 0, running: false };
  }
  const end = attempt.running ? now : attempt.end;
  if (end == null && !attempt.running) {
    return { usage: change?.usage, totalSeconds: 0, running: false };
  }
  const endMs = attempt.running ? now : new Date(end).getTime();
  const totalSeconds = Math.max(0, Math.round((endMs - new Date(attempt.start).getTime()) / 1000));
  return {
    usage: change?.usage,
    totalSeconds,
    running: attempt.running,
  };
}

export function formatDuration(startDate, endDate, { running = false } = {}) {
  if (!startDate) return "";
  if (endDate == null && !running) return "";
  const end = endDate == null ? new Date() : new Date(endDate);
  const seconds = Math.round((end - new Date(startDate)) / 1000);
  return formatDurationSeconds(seconds, { running });
}

export function formatDurationSeconds(seconds, { running = false, total = false } = {}) {
  if (!seconds && !running) return "";
  if (seconds < 1) {
    const label = running ? "running for <1s" : "<1s";
    return total && !running ? `${label} total` : label;
  }
  if (seconds < 60) {
    const label = running ? `running for ${seconds}s` : `${seconds}s`;
    return total && !running ? `${label} total` : label;
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    const label = running ? `running for ${minutes}m` : `${minutes}m`;
    return total && !running ? `${label} total` : label;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  const short = remainingMinutes ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  const label = running ? `running for ${short}` : short;
  return total && !running ? `${label} total` : label;
}

export function formatTotalDuration(change, now = Date.now()) {
  const { totalSeconds, running } = changeTotals(change, now);
  return formatDurationSeconds(totalSeconds, { running: false, total: !running });
}

export function formatCost(costUsd) {
  const n = Number(costUsd);
  if (!n) return "";
  return `$${n.toFixed(n < 0.01 ? 4 : 2)}`;
}

export function formatTokens(tokens) {
  if (!tokens) return "";
  return tokens >= 1000 ? `${Math.round(tokens / 100) / 10}k tokens` : `${tokens} tokens`;
}

export function formatUsage(usage) {
  if (!usage) return "";
  return [formatCost(usage.cost_usd), formatTokens((usage.tokens_sent || 0) + (usage.tokens_received || 0))]
    .filter(Boolean)
    .join(" · ");
}

export function parseAgentLogEntry(line) {
  if (line && typeof line === "object") {
    return {
      at: line.at || null,
      message: line.message || String(line),
    };
  }
  return { at: null, message: String(line) };
}

export function formatAgentLogTime(at) {
  if (!at) return null;
  return new Date(at).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}
